import { saveAs } from 'file-saver';
import React from 'react';
import { useBeforeunload } from 'react-beforeunload';
import { Badge, Col, Container, Image, Row } from 'react-bootstrap';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFetchAccount } from '../api/account';
import { useFetchDesign, useGenerateDesign } from '../api/designs';
import Button from '../components/Button';
import Navbar from '../components/Navbar';
import MiniProduct from '../components/Order/MiniProduct';
import Preloader from '../components/Preloader';
import ToastSpinner from '../components/ToastSpinner';
import { Routes } from '../routes';
import DesignTool from './DesignTool';

const Designer = (props) => {
  const { designer, design } = props;
  const history = useHistory();

  return (
    <div>
      <div>
        <h5>Designer</h5>
        {!designer?.is_pro && (
          <div
            style={{ fontSize: '14px' }}
            className="text-muted text-danger fst-italic mb-1 d-flex align-items-center"
          >
            <span>Upgrade your account to see designer information</span>
            <Button
              className="text-underline p-0 ms-2"
              variant="link"
              size="sm"
              onClick={() => history.push(Routes.Payment.path)}
            >
              Payment
            </Button>
          </div>
        )}
      </div>
      <div className="px-3 py-2 mt-2 border border-gray-300 rounded bg-white">
        <ul className="ps-0 list-style-none text-muted">
          <li className="mb-1">
            <span className="fw-medium">Name: </span>
            <span>{designer?.name}</span>
          </li>
          <li className="mb-1">
            <span className="fw-medium">Email: </span>
            <span>{designer?.email}</span>
          </li>
          <li className="mb-1">
            <span className="fw-medium">Whatsapp: </span>
            <span>{designer?.whatsapp}</span>
          </li>
          <li className="mb-1">
            <span className="fw-medium">Phone number: </span>
            <span>{designer?.phone}</span>
          </li>
          {design?.note && (
            <li className="mb-1">
              <span className="fw-medium">Noted: </span>
              <span>{design.note}</span>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

const ButtonDownloadDesign = (props) => {
  const { id, type = 'pdf', tier } = props;
  const history = useHistory();

  const url = new URL(window.location.href);
  const reset = !!url.searchParams.get('reset');

  const needUpgrade = !!(
    (type === 'pdf' || type === 'pdf-prev' || type === 'png') &&
    (!tier || tier === 'free')
  );

  const buttonLabel =
    type === 'pdf-prev' ? 'PDF ( include background)' : type.toUpperCase();

  const { mutate: downloadDesign, isLoading } = useGenerateDesign(reset);

  useBeforeunload((event) => {
    if (isLoading) {
      event.preventDefault();
    }
  });

  const handleDownloadPDF = () => {
    if (needUpgrade) {
      history.push(Routes.Payment.path);
    }

    downloadDesign(
      { id, type },
      {
        onSuccess: (res) => {
          const data = res?.data ? res.data : res;
          if (data.url) saveAs(data.url, data.name);
        },
        onError: (err) => {
          toast.error(err.message);
        },
      }
    );
  };

  return (
    <>
      <div className="w-100 d-flex align-items-center justify-content-between">
        <Button
          onClick={handleDownloadPDF}
          disabled={isLoading}
          variant={needUpgrade ? 'outline-danger' : 'outline-primary'}
          className="w-100"
        >
          <span>
            {isLoading ? 'Downloading ...' : `Download Design ${buttonLabel}`}
          </span>
          {needUpgrade && (
            <Badge className="ms-2" pill bg="danger">
              PRO
            </Badge>
          )}
        </Button>
      </div>
      {isLoading && (
        <div
          className="position-fixed top-0 left-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 999,
            transition: 'all 0.25s',
            backgroundColor: 'rgba(255, 255, 255, 98%)',
          }}
        >
          <ToastSpinner />
        </div>
      )}
    </>
  );
};

const DesignPage = () => {
  const { id } = useParams();

  const { data: design, isLoading, isError, error } = useFetchDesign(id);

  const dataUser = useFetchAccount();

  const [showDesignTool, setShowDesignTool] = React.useState(false);

  if (isLoading || dataUser.isLoading) {
    return <Preloader isFullHeight />;
  }

  if (isError) {
    toast.error(error.message);
  }

  if (showDesignTool) {
    const task = 'edit';
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 9999,
        }}
      >
        <DesignTool
          onClose={() => setShowDesignTool(false)}
          productId={design?.product?.id}
          designId={design.id}
          task={task}
        />
      </div>
    );
  }

  return (
    <>
      <Navbar path="design" />
      <Container fluid>
        <Row style={{ height: 'calc(100vh - 68px)' }}>
          <Col
            md={6}
            className="d-flex justify-content-center align-items-center h-100"
          >
            <div
              style={{ height: 'calc(100vh - 68px)' }}
              className="w-100 d-flex flex-column py-3 justify-content-between"
            >
              <div
                className="position-relative w-100 bg-gray-200"
                style={{
                  paddingBottom: '100%',
                  height: 0,
                }}
              >
                <Image
                  src={
                    design?.is_uploader
                      ? design.design_image?.url
                      : design?.preview?.design.url
                  }
                  className="w-100 h-100 object-fit-contain position-absolute"
                />
              </div>
              <div className="d-grid gap-2">
                {!design?.is_uploader && (
                  <>
                    <ButtonDownloadDesign
                      id={id}
                      type="pdf-prev"
                      tier={dataUser?.data?.tier}
                    />
                    <ButtonDownloadDesign
                      id={id}
                      type="pdf"
                      tier={dataUser?.data?.tier}
                    />
                    <ButtonDownloadDesign
                      id={id}
                      type="png"
                      tier={dataUser?.data?.tier}
                    />
                  </>
                )}
                <>
                  <Button
                    className="w-100"
                    variant={
                      !dataUser?.data?.tier || dataUser.data.tier === 'free'
                        ? 'outline-danger'
                        : 'outline-primary'
                    }
                    onClick={() => {
                      saveAs(design.design_image.url, design.design_image.name);
                    }}
                  >
                    <span>
                      {!design?.is_uploader
                        ? 'Download Design SVG'
                        : 'Download design file'}
                    </span>
                    {(!dataUser?.data?.tier || dataUser.data.tier === 'free') && (
                      <Badge className="ms-2" pill bg="danger">
                        PRO
                      </Badge>
                    )}
                  </Button>
                </>
              </div>
            </div>
          </Col>
          <Col md={6} className="py-3">
            <div className="d-flex flex-column justify-content-between h-100">
              <div>
                {design.product && (
                  <div className="mb-4">
                    <h5 className="mb-2">Product</h5>
                    <MiniProduct
                      productImage={design.product.product_image?.url}
                      href={`/product/${design.product.id}`}
                      name={design.product.name}
                      className="py-0"
                    />
                  </div>
                )}
                <div className="mb-4">
                  <h5>Info</h5>
                  {design.project && (
                    <div className="d-flex justify-content-between border-bottom border-gray-300 pb-2 mb-2 align-items-end">
                      <span>Orders</span>
                      <span>
                        <Link to={`/order/${design.project.id}`} target="_blank">
                          {design.project.name}
                        </Link>
                      </span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between border-bottom border-gray-300 pb-2 mb-2 align-items-end">
                    <span>Quantity</span>
                    <span>{design.quantity ? design.quantity : 1}</span>
                  </div>
                  {design.layers && (
                    <div className="d-flex justify-content-between pb-2 mb-2 align-items-end">
                      <span>Layers</span>
                      <span>
                        {design.layers.length}{' '}
                        {design.layers.length > 1 ? 'layers' : 'layer'}
                      </span>
                    </div>
                  )}
                </div>
                <Designer designer={design?.designer} design={design} />
              </div>
              {!design?.is_uploader && (
                <div className="d-flex justify-content-end align-items-end mt-3">
                  <span>
                    <Button
                      onClick={() => setShowDesignTool(true)}
                      variant="primary"
                    >
                      Edit design
                    </Button>
                  </span>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default DesignPage;
